import React, { useState, useEffect, useRef } from "react";
import { Input, MessageBox } from "react-chat-elements";
import {
  PlusCircleIcon,
  PaperAirplaneIcon,
  ArrowLeftIcon,
  PaperClipIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  BoltIcon,
} from "@heroicons/react/24/outline";
import FileSubmission from "../FileSubmission";
import { Message } from "./types";
import {
  getMessages,
  sendTextMessage,
  createMessageBox,
} from "./utils/messageView";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "server";
import Logo from "assets/img/logoJaune.png";
import {
  activateConversation,
  deactivateAnswerMessage,
} from "server/Exercises CRUD/Requests";
import { toast } from "react-hot-toast";

interface ChatContainerProps {
  messages: Message[];
  onSendMessage: (message: string) => void;
  onHandleClosePopup: () => void;
  conversationId: string;
  supervisorId?: string;
  correctorId?: string;
  isActivated?: boolean;
  userId?: string;
  conversationTitle?: string;
  onBack?: () => void;
  userRole?: string;
}

// Fonction pour créer le titre de la conversation (style Assistance)
export const createTitle = (title: string) => (
  <div className="flex items-center space-x-2">
    <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
      <img src={Logo} alt="Logo" className="w-full h-full object-contain" />
    </div>
    <div className="flex flex-col">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <p className="text-xs text-gray-500">Messages</p>
    </div>
  </div>
);

const ChatContainer: React.FC<ChatContainerProps> = ({
  messages = [],
  onSendMessage,
  onHandleClosePopup,
  conversationId,
  supervisorId = "",
  correctorId = "",
  isActivated = false,
  userId = "",
  conversationTitle = "Conversation",
  onBack,
  userRole = "student",
}) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [isFileSubmissionOpen, setIsFileSubmissionOpen] =
    useState<boolean>(false);
  const [showActivationModal, setShowActivationModal] =
    useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<{
    loading: boolean;
    success: boolean;
    error: string | null;
    progress: number;
  }>({
    loading: false,
    success: false,
    error: null,
    progress: 0,
  });
  const [showScrollButton, setShowScrollButton] = useState<boolean>(false);
  const [hasNewMessages, setHasNewMessages] = useState<boolean>(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textInput = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [activationStatus, setActivationStatus] = useState<{
    [id: string]: "success" | "error" | null;
  }>({});
  const [activatedMessages, setActivatedMessages] = useState<Set<string>>(
    new Set(),
  );

  // L'état activé/désactivé des réponses vient de l'API (isAnswerActivated)
  useEffect(() => {
    setActivatedMessages(
      new Set(
        (messages || [])
          .filter((message) => message?.isAnswerActivated)
          .map((message) => message.id),
      ),
    );
  }, [messages]);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const previousMessagesLengthRef = useRef<number>(0);
  const shouldAutoScrollRef = useRef<boolean>(true);

  // Fonction pour scroll vers le bas
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Détection mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, userId, supervisorId]);

  // Scroll vers le bas seulement si il y a de nouveaux messages
  useEffect(() => {
    const currentMessagesLength = messages.length;
    const previousMessagesLength = previousMessagesLengthRef.current;

    // Détecter s'il y a de nouveaux messages
    if (
      previousMessagesLength > 0 &&
      currentMessagesLength > previousMessagesLength
    ) {
      setHasNewMessages(true);
    }

    // Scroll automatique seulement si :
    // 1. C'est le premier chargement (previousMessagesLength === 0)
    // 2. Il y a de nouveaux messages (currentMessagesLength > previousMessagesLength)
    // 3. L'utilisateur n'a pas désactivé le scroll automatique
    if (
      (previousMessagesLength === 0 ||
        currentMessagesLength > previousMessagesLength) &&
      shouldAutoScrollRef.current
    ) {
      scrollToBottom();
      setHasNewMessages(false); // Réinitialiser car on a scrollé automatiquement
    }

    // Mettre à jour la référence pour la prochaine comparaison
    previousMessagesLengthRef.current = currentMessagesLength;
  }, [messages]);

  const handleSendMessage = async (): Promise<void> => {
    if (!inputValue?.trim()) return;

    // Forcer le scroll automatique quand l'utilisateur envoie un message
    shouldAutoScrollRef.current = true;

    try {
      await sendTextMessage(conversationId, inputValue);
      onSendMessage(inputValue);
      setInputValue("");
      clearInput(); // S'assurer que l'input est vidé
    } catch (error) {
      console.error("Erreur lors de l'envoi du message:", error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    // Entrée seule va à la ligne
    if (e.key === "Enter" && !e.ctrlKey && !e.metaKey) {
      // Laisser le comportement par défaut (aller à la ligne)
      return;
    }

    // Ctrl+Entrée ou Cmd+Entrée envoie le message
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value || "");
  };

  const handleInputFocus = (): void => {
    // Le popup ne devrait s'afficher que dans des conditions spécifiques
    // Par exemple, quand il n'y a pas de soumissions ou quand l'utilisateur n'a pas le droit d'envoyer des messages
    // Pour l'instant, on désactive l'affichage automatique du popup
    // setShowPopup(true);
  };

  const handleInputBlur = (): void => {
    setTimeout(() => {
      setShowPopup(false);
    }, 200);
  };

  const handlePopupClick = (): void => {
    onHandleClosePopup();
    navigate("/submissions");
  };

  const handleOpenFileSubmission = (): void => {
    setIsFileSubmissionOpen(true);
  };

  const handleCloseFileSubmission = (): void => {
    setIsFileSubmissionOpen(false);
  };

  const handleFilesUploaded = async (files: File[]) => {
    try {
      // Forcer le scroll automatique quand l'utilisateur envoie des fichiers
      shouldAutoScrollRef.current = true;

      // Initialiser le statut d'upload
      setUploadStatus({
        loading: true,
        success: false,
        error: null,
        progress: 0,
      });

      let successCount = 0;
      let errorCount = 0;
      const totalFiles = files.length;

      // Envoyer chaque fichier individuellement en tant que binaire
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append("files", file);

        try {
          const response = await fetch(
            `${BASE_URL}/conversations/${conversationId}/files/add`,
            {
              method: "POST",
              credentials: "include",
              body: formData,
            },
          );

          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
            console.error(`Erreur lors de l'upload du fichier ${file.name}`);
          }

          // Mettre à jour le progrès
          const progress = ((i + 1) / totalFiles) * 100;
          setUploadStatus((prev) => ({
            ...prev,
            progress,
          }));
        } catch (error) {
          errorCount++;
          console.error(
            `Erreur lors de l'upload du fichier ${file.name}:`,
            error,
          );
        }
      }

      // Définir le statut final
      if (errorCount === 0) {
        setUploadStatus({
          loading: false,
          success: true,
          error: null,
          progress: 100,
        });
        // Fermer le modal automatiquement en cas de succès complet
        setIsFileSubmissionOpen(false);
        // Rafraîchir les messages pour afficher les fichiers uploadés
        onSendMessage(""); // Déclencher le rafraîchissement
      } else if (successCount === 0) {
        setUploadStatus({
          loading: false,
          success: false,
          error: `Échec du téléchargement de tous les fichiers (${errorCount}/${totalFiles})`,
          progress: 100,
        });
      } else {
        setUploadStatus({
          loading: false,
          success: true,
          error: `Téléchargement partiel réussi (${successCount}/${totalFiles} fichiers)`,
          progress: 100,
        });
        // Fermer le modal automatiquement en cas de succès partiel
        setIsFileSubmissionOpen(false);
        // Rafraîchir les messages même en cas de succès partiel
        onSendMessage(""); // Déclencher le rafraîchissement
      }

      // Réinitialiser le statut après 3 secondes
      setTimeout(() => {
        setUploadStatus({
          loading: false,
          success: false,
          error: null,
          progress: 0,
        });
      }, 3000);
    } catch (error) {
      console.error("Erreur lors du téléchargement des fichiers:", error);
      setUploadStatus({
        loading: false,
        success: false,
        error: "Erreur générale lors du téléchargement",
        progress: 0,
      });
    }
  };

  const clearInput = (): void => {
    setInputValue("");
    if (textInput.current) {
      textInput.current.value = "";
    }
  };

  // Validation robuste des messages avec vérifications TypeScript
  const safeMessages = React.useMemo(() => {
    // Vérifier que messages est bien un tableau
    if (!Array.isArray(messages)) {
      console.warn("Messages prop is not an array:", messages);
      return [];
    }

    // Filtrer et valider chaque message
    return messages.filter((message): message is Message => {
      // Vérifier que le message existe et n'est pas null/undefined
      if (!message) {
        console.warn("Message is null or undefined:", message);
        return false;
      }

      // Vérifier les propriétés essentielles du message
      if (typeof message !== "object") {
        console.warn("Message is not an object:", message);
        return false;
      }

      // Vérifier que la date existe et est valide
      if (!message.date) {
        console.warn("Message has no date:", message);
        return false;
      }

      // Vous pouvez ajouter d'autres validations selon votre interface Message
      // Par exemple, vérifier que le contenu du message existe
      if (
        message.text !== undefined &&
        message.text !== null &&
        typeof message.text !== "string"
      ) {
        console.warn("Message text is not a string:", message);
        return false;
      }

      return true;
    });
  }, [messages]);

  const sortedMessages = React.useMemo(() => {
    return [...safeMessages].sort((a, b) => {
      try {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();

        // Vérifier que les dates sont valides
        if (isNaN(dateA) || isNaN(dateB)) {
          console.warn("Invalid date found in messages:", {
            a: a.date,
            b: b.date,
          });
          return 0;
        }

        return dateA - dateB;
      } catch (error) {
        console.error("Error sorting messages:", error);
        return 0;
      }
    });
  }, [safeMessages]);

  const handleActivateMessage = async (messageId: string) => {
    try {
      const response = await fetch(
        `${BASE_URL}/problems/messages/${messageId}/activate-answer`,
        {
          method: "POST",
          credentials: "include",
        },
      );
      if (response.ok) {
        setActivationStatus((prev) => ({ ...prev, [messageId]: "success" }));
        // Ajouter le message à la liste des messages activés
        setActivatedMessages((prev) => new Set([...prev, messageId]));
        setTimeout(
          () => setActivationStatus((prev) => ({ ...prev, [messageId]: null })),
          2000,
        );
      } else {
        setActivationStatus((prev) => ({ ...prev, [messageId]: "error" }));
        setTimeout(
          () => setActivationStatus((prev) => ({ ...prev, [messageId]: null })),
          2000,
        );
      }
    } catch (error) {
      setActivationStatus((prev) => ({ ...prev, [messageId]: "error" }));
      setTimeout(
        () => setActivationStatus((prev) => ({ ...prev, [messageId]: null })),
        2000,
      );
    }
  };

  const handleDeactivateMessage = async (messageId: string) => {
    try {
      const response = await deactivateAnswerMessage(messageId);
      if (response.status === 200 || response.status === 201) {
        setActivatedMessages((prev) => {
          const next = new Set(prev);
          next.delete(messageId);
          return next;
        });
        toast.success("Réponse désactivée : l'élève ne la voit plus.");
      } else {
        toast.error(
          (response.body as { message?: string })?.message ||
            "Impossible de désactiver cette réponse.",
        );
      }
    } catch (error) {
      toast.error("Impossible de désactiver cette réponse.");
    }
  };

  const handleActivateConversation = async () => {
    const promise = activateConversation(conversationId);

    toast.promise(promise, {
      loading: "Activation en cours...",
      success: (res) => {
        if (res.status === 200) {
          setShowActivationModal(false);
          return "Conversation activée avec succès !";
        } else {
          throw new Error(
            (res.body as { message: string })?.message ||
              "Une erreur est survenue.",
          );
        }
      },
      error: (err) => err.message || "Impossible d'activer la conversation.",
    });
  };

  return (
    <div className="flex flex-col h-full w-full min-h-0">
      {/* Header fixe (style Assistance) */}
      <div className="flex-shrink-0 flex flex-row bg-[#F1FBFF] space-x-2 p-4 border-b border-gray-200">
        {onBack && (
          <button
            className="text-black rounded px-1 py-1 flex items-center justify-center sm:hidden"
            onClick={onBack}
          >
            <ArrowLeftIcon className="w-5 h-5 stroke-2" />
          </button>
        )}
        {createTitle(conversationTitle)}

        {/* Bouton d'activation de conversation */}
        {userId === supervisorId && !isActivated && (
          <button
            onClick={() => setShowActivationModal(true)}
            className="ml-auto text-yellow-500 hover:bg-yellow-50 p-2 rounded-lg transition-colors duration-200"
            title="Activer l'exercice pour la conversation"
          >
            <BoltIcon className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Zone de contenu avec défilement */}
      <div className="flex-1 flex flex-col min-h-0">
        <div className="flex flex-col h-full w-full min-h-0">
          {/* Zone des messages avec défilement */}
          <div
            className="flex-1 overflow-y-auto p-4 min-h-0 relative"
            onScroll={(e) => {
              const target = e.target as HTMLDivElement;
              const { scrollTop, scrollHeight, clientHeight } = target;
              const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // Tolérance de 10px

              // Si l'utilisateur n'est pas en bas, désactiver le scroll automatique
              if (!isAtBottom) {
                shouldAutoScrollRef.current = false;
                setShowScrollButton(hasNewMessages); // Afficher le bouton si il y a de nouveaux messages
              } else {
                shouldAutoScrollRef.current = true;
                setShowScrollButton(false); // Masquer le bouton quand on est en bas
                setHasNewMessages(false); // Réinitialiser car on est en bas
              }
            }}
          >
            <div className="space-y-4">
              {sortedMessages.map((message, index) => {
                try {
                  // Validation supplémentaire avant de créer le message box
                  if (!message) {
                    console.warn(
                      `Message at index ${index} is null or undefined`,
                    );
                    return null;
                  }

                  // Le bouton d'activation doit être visible uniquement pour le superviseur de l'exercice
                  // UX mobile-friendly : bouton toujours visible sur mobile, au survol sur desktop
                  // Les messages du superviseur ne peuvent pas être activés
                  const isSupervisorMessage =
                    (userId === supervisorId && message.title === "Vous") ||
                    message.title === "Administrateur" ||
                    message.title === "Admin École";
                  const showButton =
                    userId === supervisorId &&
                    !activatedMessages.has(message.id) &&
                    !isSupervisorMessage &&
                    (isMobile || hoveredMessageId === message.id);
                  // Seules les réponses du correcteur peuvent être désactivées
                  const showDeactivateButton =
                    userId === supervisorId &&
                    !!correctorId &&
                    message.senderId === correctorId &&
                    (isMobile || hoveredMessageId === message.id);

                  return (
                    <div
                      key={message.id}
                      className="relative group"
                      onMouseEnter={() => setHoveredMessageId(message.id)}
                      onMouseLeave={() => setHoveredMessageId(null)}
                    >
                      {createMessageBox(message, index)}

                      {/* Bouton d'activation pour le superviseur uniquement - UX mobile-friendly */}
                      {showButton && (
                        <div
                          className={`absolute -top-1 z-10 ${
                            message.position === "right"
                              ? "-right-1"
                              : "-left-1"
                          } ${
                            isMobile
                              ? "opacity-100"
                              : "opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                          }`}
                        >
                          <button
                            className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm font-medium shadow-lg transition-all duration-200 hover:scale-105 border border-green-400 active:scale-95 touch-manipulation"
                            onClick={() => handleActivateMessage(message.id)}
                            title="Activer cette réponse comme solution"
                          >
                            <span className="hidden sm:inline">Activer</span>
                            <span className="sm:hidden">✓</span>
                          </button>
                        </div>
                      )}

                      {/* Feedback d'activation - UX mobile-friendly */}
                      {activationStatus[message.id] &&
                        !activatedMessages.has(message.id) && (
                          <div
                            className={`absolute -top-1 z-20 ${
                              message.position === "right"
                                ? "-right-1"
                                : "-left-1"
                            }`}
                          >
                            {activationStatus[message.id] === "success" && (
                              <span className="text-green-600 text-sm font-medium bg-green-50 px-3 py-2 rounded border border-green-200 shadow-sm">
                                <span className="hidden sm:inline">
                                  ✓ Activé
                                </span>
                                <span className="sm:hidden">✓</span>
                              </span>
                            )}
                            {activationStatus[message.id] === "error" && (
                              <span className="text-red-600 text-sm font-medium bg-red-50 px-3 py-2 rounded border border-red-200 shadow-sm">
                                <span className="hidden sm:inline">
                                  ✗ Erreur
                                </span>
                                <span className="sm:hidden">✗</span>
                              </span>
                            )}
                          </div>
                        )}

                      {/* Indicateur pour les messages déjà activés (visible pour tous) - UX mobile-friendly */}
                      {/* Le superviseur peut désactiver une réponse du correcteur, même après l'envoi à l'élève */}
                      {activatedMessages.has(message.id) && (
                        <div
                          className={`absolute -top-1 z-10 ${
                            message.position === "right"
                              ? "-right-1"
                              : "-left-1"
                          }`}
                        >
                          {showDeactivateButton ? (
                            <button
                              className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm font-medium shadow-lg transition-all duration-200 hover:scale-105 border border-red-400 active:scale-95 touch-manipulation"
                              onClick={() =>
                                handleDeactivateMessage(message.id)
                              }
                              title="Désactiver cette réponse : l'élève ne la verra plus"
                            >
                              <span className="hidden sm:inline">
                                Désactiver
                              </span>
                              <span className="sm:hidden">✗</span>
                            </button>
                          ) : (
                            <span className="text-green-600 text-sm font-medium bg-green-50 px-3 py-2 rounded border border-green-200 shadow-sm">
                              <span className="hidden sm:inline">✓ Activé</span>
                              <span className="sm:hidden">✓</span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                } catch (error) {
                  console.error(
                    `Erreur lors du rendu du message à l'index ${index}:`,
                    error,
                  );
                  console.error("Message data:", message);

                  // Retourner un élément de fallback au lieu de null pour éviter les erreurs de rendu
                  return (
                    <div
                      key={`error-${index}`}
                      className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm"
                    >
                      <div className="flex items-center space-x-2">
                        <ExclamationTriangleIcon className="w-4 h-4" />
                        <span>Erreur lors du rendu du message</span>
                      </div>
                    </div>
                  );
                }
              })}
              {/* Élément invisible pour le scroll automatique */}
              <div ref={messagesEndRef} />
            </div>

            {/* Bouton de scroll vers le bas */}
            {showScrollButton && (
              <button
                onClick={() => {
                  scrollToBottom();
                  setShowScrollButton(false);
                  setHasNewMessages(false);
                  shouldAutoScrollRef.current = true;
                }}
                className="absolute bottom-4 right-4 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110 z-10"
                title="Aller aux nouveaux messages"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 14l-7 7m0 0l-7-7m7 7V3"
                  />
                </svg>
              </button>
            )}
          </div>

          {/* Zone de saisie fixe */}
          <div className="flex-shrink-0 bg-[#F1FBFF] p-4 border-t border-gray-200">
            {/* Feedback d'upload */}
            {(uploadStatus.loading ||
              uploadStatus.success ||
              uploadStatus.error) && (
              <div
                className={`mb-3 p-3 rounded-lg border ${
                  uploadStatus.loading
                    ? "bg-blue-50 border-blue-200 text-blue-800"
                    : uploadStatus.success
                    ? "bg-green-50 border-green-200 text-green-800"
                    : "bg-red-50 border-red-200 text-red-800"
                }`}
              >
                <div className="flex items-center space-x-2">
                  {uploadStatus.loading && (
                    <>
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                      <span>Téléchargement en cours...</span>
                    </>
                  )}
                  {uploadStatus.success && !uploadStatus.loading && (
                    <>
                      <CheckCircleIcon className="w-5 h-5 text-green-600" />
                      <span>Téléchargement terminé !</span>
                    </>
                  )}
                  {uploadStatus.error && !uploadStatus.loading && (
                    <>
                      <XCircleIcon className="w-5 h-5 text-red-600" />
                      <span>{uploadStatus.error}</span>
                    </>
                  )}
                </div>

                {/* Barre de progression */}
                {uploadStatus.loading && (
                  <div className="mt-2">
                    <div className="w-full bg-blue-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadStatus.progress}%` }}
                      ></div>
                    </div>
                    <div className="text-xs mt-1 text-blue-600">
                      {Math.round(uploadStatus.progress)}% terminé
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="relative">
              <Input
                className="bg-white rounded-lg"
                placeholder="Saisissez votre message"
                value={inputValue}
                multiline={true}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                referance={textInput}
                onSubmit={() => {
                  setInputValue("");
                }}
                leftButtons={
                  <button
                    className="bg-[#131CC9] text-white rounded px-1 py-1 flex items-center justify-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenFileSubmission();
                    }}
                  >
                    <PlusCircleIcon className="w-5 h-5 stroke-2" />
                  </button>
                }
                rightButtons={
                  <button
                    className="bg-[#131CC9] text-white rounded px-1 py-1 flex items-center justify-center"
                    onClick={(e) => {
                      handleSendMessage();
                    }}
                  >
                    <PaperAirplaneIcon className="w-5 h-5 stroke-2" />
                  </button>
                }
                maxHeight={60}
              />
              {/* Popup temporairement désactivé pour éviter l'affichage intempestif sur mobile */}
              {/* {showPopup && (
                <div
                  className="absolute bottom-full left-0 mb-2 bg-white rounded-lg shadow-lg p-4 w-64 cursor-pointer"
                  onClick={handlePopupClick}
                >
                  <p className="text-sm text-gray-600">
                    Aucune soumission n'a été enregistrée. Cliquez ici pour accéder
                    à la page des soumissions.
                  </p>
                </div>
              )} */}
            </div>
          </div>
        </div>
      </div>

      {/* Composant de soumission de fichiers */}
      {isFileSubmissionOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl border border-gray-100">
            {/* Header inspiré de submissionDetails */}
            <div className="relative p-6 bg-[#E3B500] text-white flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-[#E3B500] font-bold">
                  <PaperClipIcon className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Ajouter des fichiers</h2>
                  <p className="text-blue-100">
                    Sélectionnez les fichiers à envoyer
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseFileSubmission}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <PlusCircleIcon className="w-6 h-6 transform rotate-45" />
              </button>
            </div>

            {/* Contenu */}
            <div className="flex-1 overflow-hidden">
              <FileSubmission
                conversationId={conversationId}
                onSubmit={handleFilesUploaded}
              />
            </div>

            {/* Footer inspiré de submissionDetails */}
            <div className="p-4 bg-gray-50 border-t flex justify-end">
              <button
                onClick={handleCloseFileSubmission}
                className="px-4 py-2 bg-[#E3B500] text-white rounded-lg hover:bg-[#D4A500] transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmation pour l'activation de conversation */}
      {showActivationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg w-full max-w-md shadow-2xl border border-gray-100">
            {/* Header */}
            <div className="relative p-6 bg-[#E3B500] text-white flex justify-between items-center">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-[#E3B500] font-bold">
                  <BoltIcon className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Activer la conversation</h2>
                  <p className="text-[#E3B500]">Confirmer l'activation</p>
                </div>
              </div>
            </div>

            {/* Contenu */}
            <div className="p-6">
              <p className="text-gray-700 mb-6">
                Êtes-vous sûr de vouloir activer cette conversation ? Cette
                action déclenche l’envoie à l’élève, des réponses du correcteur
                que vous avez activée.
              </p>

              <div className="flex space-x-3 justify-end">
                <button
                  onClick={() => setShowActivationModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleActivateConversation}
                  className="px-4 py-2 bg-[#E3B500] text-white rounded-lg hover:bg-[#D1A305] transition-colors flex items-center space-x-2"
                >
                  <BoltIcon className="w-4 h-4" />
                  <span>Activer</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatContainer;
